import { useSearchParams } from 'next/navigation';
import { useCallback, useState } from 'react';

import { useRouter } from '@bprogress/next';

import UIResourceHtml from '@/components/UIResource/Html';
import { useUIResourcePool } from '@/ducks/common/resources';
import { FILTER_KEY } from '@/ducks/filters/types';
import { selectFilteredPackages, selectFilteredSailings } from '@/ducks/pages/chooseVoyage/selectors';
import { QF_SEARCH_PARAMS } from '@/features/quickFindFilters/searchParams';
import { useAppSelector } from '@/store';

import Button from '../elements/Button';

import styles from './ResetVoyageIdsBanner.module.scss';

const RESOURCE_POOL = {
  cta: 'SailingID.quickRemoveBanner.CTA',
  description: 'SailingID.quickRemoveBanner.description',
  title: 'SailingID.quickRemoveBanner.title',
} as const;

const ResetVoyageIdsBanner = () => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [isHidden, setIsHidden] = useState(false);

  const { cta, description, title } = useUIResourcePool(RESOURCE_POOL);

  const packages = useAppSelector(selectFilteredPackages);
  const sailings = useAppSelector(selectFilteredSailings);
  const uniquePackageCodes = new Set(sailings?.map((sailing) => sailing.packageCode)).size;

  const voyageIds = searchParams.get(FILTER_KEY.voyageIds);
  const quickFindId = searchParams.get(QF_SEARCH_PARAMS.quickFindID);

  const handleReset = useCallback(() => {
    setIsHidden(true);
    const newParams = new URLSearchParams(searchParams.toString());
    newParams.delete('voyageIds');
    router.replace(`?${newParams.toString()}`);
  }, [router, searchParams]);

  if (!voyageIds || quickFindId || packages.length === 0 || isHidden) {
    return null;
  }

  return (
    <div className={styles.banner}>
      <div className={styles.content}>
        <span className={styles.emoji}>
          <UIResourceHtml id="SailingID.quickRemoveBanner.emoji" />
        </span>
        <div className={styles.text}>
          <p>
            <strong className={styles.title}>{title.replace('{N}', uniquePackageCodes.toString())}</strong>{' '}
            {description}
          </p>
        </div>
        <Button className={styles.resetButton} onClick={handleReset} size="md" variant="secondary">
          {cta}
        </Button>
      </div>
    </div>
  );
};

export default ResetVoyageIdsBanner;
